import { useState, useEffect } from "react";
import api from "../lib/api";
import {
  Send, Plus, Mail, MessageSquare, BarChart3, Users,
  Clock, Edit2, Trash2, Sparkles, Eye, MousePointerClick
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const statusBadge = {
  draft: { bg: "bg-[#FAFAFA]", text: "text-slate-600" },
  scheduled: { bg: "bg-[#EAF0F6]", text: "text-blue-600" },
  sent: { bg: "bg-[#E8F0E9]", text: "text-emerald-600" },
  failed: { bg: "bg-rose-50", text: "text-rose-600" },
};

export default function MarketingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "", type: "email", subject: "", body: "", audience: "all", status: "draft"
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get("/campaigns");
      setCampaigns(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await api.post("/campaigns", newCampaign);
      toast.success("Campaign created");
      setShowNew(false);
      setNewCampaign({ name: "", type: "email", subject: "", body: "", audience: "all", status: "draft" });
      fetchCampaigns();
    } catch (err) { toast.error("Failed to create campaign"); }
  };

  const handleSend = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/send`);
      toast.success("Campaign sent successfully");
      fetchCampaigns();
    } catch (err) { toast.error("Failed to send campaign"); }
  };

  const handleDelete = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const generateAICopy = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/ai/marketing-copy", null, {
        params: {
          campaign_type: newCampaign.type,
          target_audience: newCampaign.audience,
          tone: "professional",
          details: newCampaign.name || "salon promotion"
        }
      });
      setNewCampaign(p => ({
        ...p,
        subject: res.data.subject || p.subject,
        body: res.data.body || res.data.preview || p.body
      }));
      toast.success("AI copy generated!");
    } catch (err) {
      toast.error("AI generation failed - try again");
    } finally { setAiLoading(false); }
  };

  const totalSent = campaigns.reduce((s, c) => s + (c.sent_count || 0), 0);
  const totalOpens = campaigns.reduce((s, c) => s + (c.open_count || 0), 0);
  const totalClicks = campaigns.reduce((s, c) => s + (c.click_count || 0), 0);
  const totalBookings = campaigns.reduce((s, c) => s + (c.booking_count || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="marketing-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Marketing</h1>
          <p className="text-slate-600 text-sm mt-1">Campaigns and automation</p>
        </div>
        <Button data-testid="new-campaign-btn" onClick={() => setShowNew(true)}
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Sent", value: totalSent, icon: Send },
          { label: "Opens", value: totalOpens, icon: Eye, sub: totalSent ? `${((totalOpens/totalSent)*100).toFixed(0)}% rate` : "" },
          { label: "Clicks", value: totalClicks, icon: MousePointerClick },
          { label: "Bookings", value: totalBookings, icon: BarChart3, sub: totalClicks ? `${((totalBookings/Math.max(totalClicks,1))*100).toFixed(0)}% conversion` : "" },
        ].map(stat => (
          <div key={stat.label} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{stat.label}</span>
            </div>
            <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{stat.value}</p>
            {stat.sub && <p className="text-xs text-rose-400 mt-0.5">{stat.sub}</p>}
          </div>
        ))}
      </div>

      {/* Campaign List */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-slate-200">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Campaign</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Type</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Sent</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Opens</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Bookings</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(c => {
              const badge = statusBadge[c.status] || statusBadge.draft;
              return (
                <tr key={c.campaign_id} className="border-b border-slate-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`campaign-${c.campaign_id}`}>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-slate-900">{c.name}</p>
                    {c.subject && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{c.subject}</p>}
                  </td>
                  <td className="px-4 py-4">
                    <span className="flex items-center gap-1.5 text-sm text-slate-600">
                      {c.type === "email" ? <Mail className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
                      {c.type}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-4 text-sm text-right text-slate-900">{c.sent_count || 0}</td>
                  <td className="px-4 py-4 text-sm text-right text-slate-900">{c.open_count || 0}</td>
                  <td className="px-4 py-4 text-sm text-right font-medium text-emerald-600">{c.booking_count || 0}</td>
                  <td className="px-4 py-4">
                    <div className="flex gap-1 justify-end">
                      {c.status === "draft" && (
                        <button data-testid={`send-${c.campaign_id}`} onClick={() => handleSend(c.campaign_id)} className="p-1.5 rounded-md hover:bg-[#E8F0E9] text-emerald-600 transition-colors" title="Send">
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(c.campaign_id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600 transition-colors" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {campaigns.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-400">No campaigns yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New Campaign Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 rounded-2xl p-8" data-testid="new-campaign-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">Create Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="campaign-name" value={newCampaign.name} onChange={(e) => setNewCampaign(p => ({ ...p, name: e.target.value }))} placeholder="Campaign name" className="h-11 border-slate-200 bg-white rounded-lg" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={newCampaign.type} onValueChange={(v) => setNewCampaign(p => ({ ...p, type: v }))}>
                <SelectTrigger className="h-11 border-slate-200 bg-white rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newCampaign.audience} onValueChange={(v) => setNewCampaign(p => ({ ...p, audience: v }))}>
                <SelectTrigger className="h-11 border-slate-200 bg-white rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  <SelectItem value="active">Active Clients</SelectItem>
                  <SelectItem value="vip">VIP Clients</SelectItem>
                  <SelectItem value="at-risk">At-Risk Clients</SelectItem>
                  <SelectItem value="lead">Leads</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="campaign-subject" value={newCampaign.subject} onChange={(e) => setNewCampaign(p => ({ ...p, subject: e.target.value }))} placeholder="Subject line" className="h-11 border-slate-200 bg-white rounded-lg" />
            <Textarea data-testid="campaign-body" value={newCampaign.body} onChange={(e) => setNewCampaign(p => ({ ...p, body: e.target.value }))} placeholder="Campaign body..." rows={5} className="border-slate-200 bg-white rounded-lg" />
            <Button data-testid="ai-generate-btn" onClick={generateAICopy} disabled={aiLoading} variant="outline" className="w-full border-rose-300 text-slate-900 hover:bg-rose-50 rounded-full h-10">
              <Sparkles className="w-4 h-4 mr-2" /> {aiLoading ? "Generating..." : "Generate with AI"}
            </Button>
            <Button data-testid="create-campaign-btn" onClick={handleCreate} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">
              Create Campaign
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
